import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { TableService } from "../services/pager/pager";
import { OnboardingUsersRolesFilter } from "./onboarding-users-roles.filter";
import { SpinnerService } from "../services/spinner/spinner.service";
import { ToastService } from "../services/notification/toast.service";
import { RbacService } from "./rbac.service";
import { UserPermissionsService } from "../services/user-permissions.service";
import { TranslateService } from "@ngx-translate/core";
import { SecurityService } from "../services/security.service";

interface RoleOption {
  id: string | number;
  name: string;
  description: string;
  permissionCount: number;
  userCount: number;
  type: "system" | "custom";
  menus?: any[];
}

@Component({
  selector: "app-onboarding-users-roles",
  templateUrl: "./onboarding-users-roles.component.html",
  styleUrls: ["./onboarding-users-roles.component.scss"],
  standalone: false
})
export class OnboardingUsersRolesComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
  @ViewChild("inviteUserDialog") inviteUserDialog: TemplateRef<any> | undefined;

  private readonly maxUserFieldLength = 120;
  activeTab: "users" | "roles" = "users";
  isInviteModalOpen = false;
  isOpenSearchPanel = true;
  hideInvitePassword = true;
  filter = new OnboardingUsersRolesFilter(this.tableService.filter);
  inviteForm: FormGroup;

  statusOptions = [
    { value: 1, labelKey: "onboardingUsersRoles.status.active" },
    { value: 2, labelKey: "onboardingUsersRoles.status.inactive" }
  ];

  displayedColumns: string[] = [
    "user",
    "email_user",
    "role",
    "status_user",
    "actions",
  ];
  dataSource = new MatTableDataSource<any>([]);

  rolesDisplayedColumns: string[] = [
    "name",
    "description",
    "permissionCount",
    "userCount",
    "actions",
  ];
  rolesDataSource = new MatTableDataSource<RoleOption>([]);

  roles: RoleOption[] = [];
  permissionsReady = false;
  private inviteDialogRef: MatDialogRef<any> | null = null;
  private readonly createRolePermissions = [
    "controllers.Roles.create",
  ];
  private readonly updateRolePermissions = [
    "controllers.Roles.update",
  ];
  private readonly deleteRolePermissions = [
    "controllers.Roles.delete",
  ];

  constructor(
    public tableService: TableService<any>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private rbacService: RbacService,
    private userPermissionsService: UserPermissionsService,
    private spinner: SpinnerService,
    private toast: ToastService,
    private translate: TranslateService,
    private securityService: SecurityService,
    private dialog: MatDialog
  ) {
    this.tableService = new TableService();
    this.inviteForm = this.formBuilder.group({
      name: ["", [Validators.required, Validators.minLength(2), Validators.maxLength(this.maxUserFieldLength)]],
      lastName: ["", [Validators.required, Validators.minLength(2), Validators.maxLength(this.maxUserFieldLength)]],
      email: ["", [Validators.required, Validators.email, Validators.maxLength(this.maxUserFieldLength)]],
      password: ["", [Validators.required, this.passwordValidator]],
      roleId: ["", [Validators.required]],
    });
  }

  ngOnInit(): void {
    const restored = this.tableService.loadListState("onboarding-users-roles-list");
    if (restored && restored.filter) {
      this.restoreForBack(restored);
    }

    const tab = this.activatedRoute.snapshot.queryParamMap.get("tab");
    this.activeTab = tab === "roles" ? "roles" : "users";

    this.userPermissionsService.loadCurrentUserPermissions().subscribe({
      next: () => {
        this.permissionsReady = true;
        if (this.canListUsers()) {
          this.list();
        } else {
          this.dataSource = new MatTableDataSource<any>([]);
          this.tableService.pager = { pageIndex: 0, pageSize: 20, totalEntities: 0 };
        }
        this.loadRoles();
      },
      error: (error) => {
        this.permissionsReady = true;
        this.toast.showError(error);
      },
    });
  }

  restoreForBack(restored: any) {
    this.filter = new OnboardingUsersRolesFilter(restored.filter);
    this.tableService.clearListState("onboarding-users-roles-list");
    if (this.paginator) {
      this.paginator.pageIndex = restored.pageIndex;
      this.paginator.pageSize = restored.pageSize;
    }
  }

  list(event?: PageEvent) {
    if (!this.canListUsers()) {
      this.dataSource = new MatTableDataSource<any>([]);
      this.tableService.pager = { pageIndex: 0, pageSize: 20, totalEntities: 0 };
      return;
    }

    this.spinner.open();

    const partnerId = this.securityService.getTokenPartner();
    if (!partnerId) {
      this.spinner.close();
      this.dataSource = new MatTableDataSource<any>([]);
      this.tableService.pager = { pageIndex: 0, pageSize: 20, totalEntities: 0 };
      return this.toast.showInfo(this.translate.instant("onboardingUsersRoles.messages.missingPartnerLoadUsers"));
    }

    const pageIndex = event ? event.pageIndex : this.tableService.pager.pageIndex;
    const pageSize = event ? event.pageSize : this.tableService.pager.pageSize;

    let httpParams = new HttpParams()
      .set("pageIndex", pageIndex.toString())
      .set("pageSize", pageSize.toString())
      .set("partnerId", partnerId.toString());

    httpParams = this.filter.getHttpParams(httpParams);

    this.rbacService
      .listUsers(Number(partnerId), httpParams)
      .subscribe(
        (response) => {
          const users = this.extractUsers(response);

          this.dataSource = new MatTableDataSource<any>(users);
          this.tableService.pager = response["pager"] || {
            pageIndex: response?.data?.pageIndex || pageIndex,
            pageSize: response?.data?.pageSize || pageSize,
            totalEntities: response?.data?.totalElements || users.length,
          };
          this.tableService.selection.clear();
          this.spinner.close();
        },
        (err) => {
          this.spinner.close();
          this.dataSource = new MatTableDataSource<any>([]);
          this.tableService.pager = { pageIndex: 0, pageSize: 20, totalEntities: 0 };
          this.toast.showError(err);
        }
      );
  }

  setTab(tab: "users" | "roles") {
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  search() {
    if (!this.canListUsers()) {
      return;
    }

    this.tableService.filter = new OnboardingUsersRolesFilter(this.filter);
    this.router.navigate([], {
      queryParams: {
        name_user: this.filter.name_user,
        lastName_user: this.filter.lastName_user,
        email_user: this.filter.email_user,
        status_user: this.filter.status_user,
      },
    });
    this.list();
  }

  searchRoles(value: string) {
    const query = value.trim();
    const partnerId = this.getPartnerId();

    if (!partnerId) {
      this.rolesDataSource.data = [];
      this.toast.showInfo(this.translate.instant("onboardingUsersRoles.messages.missingPartnerSearchRoles"));
      return;
    }

    this.rbacService.listRoles(partnerId, query || undefined).subscribe(
      (response) => this.setRolesFromResponse(response),
      (error) => {
        this.rolesDataSource.data = [];
        this.toast.showError(error);
      }
    );
  }

  refreshRoles() {
    this.loadRoles();
  }

  openSearchPanel(value: boolean) {
    this.isOpenSearchPanel = value;
    this.filter = new OnboardingUsersRolesFilter(this.filter);
  }

  reset() {
    this.filter = new OnboardingUsersRolesFilter();
    this.tableService.filter = null;
    this.tableService.pager = { pageIndex: 0, pageSize: 20 };
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginator.pageSize = 20;
    }
    this.router.navigate([], { queryParams: {}, replaceUrl: true });
    if (this.canListUsers()) {
      this.list();
    }
  }

  openInviteModal() {
    if (!this.canCreateUsers()) {
      return;
    }

    if (this.inviteDialogRef || !this.inviteUserDialog) {
      return;
    }

    this.inviteForm.reset({ name: "", lastName: "", email: "", password: "", roleId: "" });
    this.hideInvitePassword = true;
    this.isInviteModalOpen = true;
    this.inviteDialogRef = this.dialog.open(this.inviteUserDialog, {
      width: "34rem",
      maxWidth: "92vw",
      autoFocus: false,
      restoreFocus: true,
    });

    this.inviteDialogRef.afterClosed().subscribe(() => {
      this.isInviteModalOpen = false;
      this.inviteDialogRef = null;
    });
  }

  closeInviteModal() {
    this.isInviteModalOpen = false;
    this.inviteDialogRef?.close();
  }

  passwordValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return null;
    }

    const hasMinLength = value.length >= 12;
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*()\\-_=+\\\[\\\]{};:,.<>?/|\\\\'""]/.test(value);

    if (hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar) {
      return null;
    }

    return {
      passwordStrength: {
        hasMinLength,
        hasUpperCase,
        hasLowerCase,
        hasNumber,
        hasSpecialChar,
      },
    };
  }

  generateInvitePassword(): void {
    const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercaseChars = "abcdefghijklmnopqrstuvwxyz";
    const numberChars = "0123456789";
    const specialChars = "!@#$%^&*()-_=+[]{};:,.<>?/|";

    let password = "";
    password += uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)];
    password += lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)];
    password += numberChars[Math.floor(Math.random() * numberChars.length)];
    password += specialChars[Math.floor(Math.random() * specialChars.length)];

    const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;
    for (let i = password.length; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    password = password.split("").sort(() => Math.random() - 0.5).join("");
    this.inviteForm.patchValue({ password });
    this.inviteForm.get("password")?.markAsTouched();
    this.toast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.passwordGenerated"));
  }

  getInvitePasswordErrors(): any {
    const passwordControl = this.inviteForm.get("password");
    if (passwordControl?.hasError("passwordStrength")) {
      return passwordControl.errors?.["passwordStrength"];
    }
    if (!passwordControl?.value) {
      return {
        hasMinLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false,
      };
    }
    return null;
  }

  sendInvitation() {
    if (!this.canCreateUsers()) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.noPermissionInviteUsers"));
      return;
    }

    if (this.inviteForm.invalid) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.completeInviteForm"));
      return;
    }

    const partnerId = this.getPartnerId();
    if (!partnerId) {
      this.toast.showInfo(this.translate.instant("onboardingUsersRoles.messages.missingPartnerInviteUser"));
      return;
    }

    const body = {
      partnerId,
      name_user: this.inviteForm.value.name,
      lastName_user: this.inviteForm.value.lastName,
      email_user: this.inviteForm.value.email,
      password: this.inviteForm.value.password,
    };

    this.spinner.open();
    this.rbacService.createOnboardingUser(body).subscribe(
      (response) => {
        const userId = this.extractCreatedUserId(response);
        if (!userId) {
          this.spinner.close();
          this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.userCreatedRoleIdMissing"));
          this.closeInviteModal();
          this.list();
          return;
        }

        this.rbacService.addRoleToUser(partnerId, userId, this.inviteForm.value.roleId).subscribe(
          () => {
            this.spinner.close();
            this.toast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.userInvited"));
            this.closeInviteModal();
            this.list();
          },
          (error) => {
            this.spinner.close();
            this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.userCreatedRoleAssignFailed"));
            this.toast.showError(error);
            this.closeInviteModal();
            this.list();
          }
        );
      },
      (error) => {
        this.spinner.close();
        this.toast.showError(error);
      }
    );
  }

  createRole() {
    if (!this.canCreateRoles()) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.noPermissionCreateRoles"));
      return;
    }

    this.router.navigate(["./roles/create"], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: "preserve",
    });
  }

  viewRole(role: RoleOption) {
    if (role.type === "custom" && !this.canUpdateRoles()) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.noPermissionEditRoles"));
      return;
    }

    this.router.navigate(["./roles", role.id, "update"], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: "preserve",
      state: { role },
    });
  }

  editRole(role: RoleOption) {
    this.viewRole(role);
  }

  deleteRole(role: RoleOption) {
    if (!this.canDeleteRoles()) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.noPermissionDeleteRoles"));
      return;
    }

    if (role.type === "system") {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.systemRoleCannotDelete"));
      return;
    }

    if (role.userCount > 0) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.roleHasUsers"));
      return;
    }

    const partnerId = this.getPartnerId();
    if (!partnerId) {
      this.toast.showInfo(this.translate.instant("onboardingUsersRoles.messages.missingPartnerDeleteRole"));
      return;
    }

    this.spinner.open();
    this.rbacService.deleteRole(partnerId, role.id).subscribe(
      () => {
        this.spinner.close();
        this.toast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.roleDeleted"));
        this.loadRoles();
      },
      (error) => {
        this.spinner.close();
        this.toast.showError(error);
      }
    );
  }

  updateUser(item: any) {
    if (!this.canUpdateUsers()) {
      return;
    }

    this.router.navigate(["/onboarding-users", item.id, "update"], {
      queryParams: { returnTo: "onboarding-users-roles", tab: "users" },
    });
  }

  deactivateUser(item: any) {
    if (!this.canDeleteUsers()) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.noPermissionDeactivateUsers"));
      return;
    }

    const partnerId = this.getPartnerId();
    if (!partnerId) {
      this.toast.showInfo(this.translate.instant("onboardingUsersRoles.messages.missingPartnerDeactivateUser"));
      return;
    }

    this.spinner.open();
    this.rbacService.deactivateUser(partnerId, item.id).subscribe(
        () => {
          this.spinner.close();
          this.toast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.userDeactivated"));
          this.list();
        },
        (error) => {
          this.spinner.close();
          this.toast.showError(error);
        }
      );
  }

  reactivateUser(item: any) {
    if (!this.canUpdateUsers()) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.noPermissionReactivateUsers"));
      return;
    }

    const partnerId = this.getPartnerId();
    if (!partnerId) {
      this.toast.showInfo(this.translate.instant("onboardingUsersRoles.messages.missingPartnerReactivateUser"));
      return;
    }

    this.spinner.open();
    this.rbacService.reactivateOnboardingUser(partnerId, item).subscribe(
      () => {
        this.spinner.close();
        this.toast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.userReactivated"));
        this.list();
      },
      (error) => {
        this.spinner.close();
        this.toast.showError(error);
      }
    );
  }

  resendInvitation(item: any) {
    if (!this.canCreateUsers()) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.noPermissionResendInvitations"));
      return;
    }

    if (!confirm(this.translate.instant("onboardingUsersRoles.messages.resendInvitationConfirm"))) {
      return;
    }

    const partnerId = this.getPartnerId();
    if (!partnerId) {
      this.toast.showInfo(this.translate.instant("onboardingUsersRoles.messages.missingPartnerResendInvitation"));
      return;
    }

    this.spinner.open();
    this.rbacService.resendOnboardingUserInvitation(partnerId, item.id).subscribe(
      () => {
        this.spinner.close();
        this.toast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.invitationResent"));
      },
      (error) => {
        this.spinner.close();
        this.toast.showError(error);
      }
    );
  }

  cancelInvitation(item: any) {
    this.deactivateUser(item);
  }

  getStatusLabel(status: number): string {
    if (status === 1) return this.translate.instant("onboardingUsersRoles.status.active");
    return this.translate.instant("onboardingUsersRoles.status.inactive");
  }

  getRoleClass(role: string | undefined): string {
    if (role === "Admin" || role === "Manager") return "admin";
    if (role === "User") return "user";
    return "custom";
  }

  getUserRoleName(user: any): string {
    return this.getUserRoles(user).join(", ");
  }

  getUserRoles(user: any): string[] {
    if (Array.isArray(user?.roles) && user.roles.length > 0) {
      return user.roles.map((role: any) => role.name).filter(Boolean);
    }

    return user?.role ? [user.role] : [];
  }

  getVisibleUserRoles(user: any): string[] {
    return this.getUserRoles(user).slice(0, 3);
  }

  getUserRolesTooltip(user: any): string {
    return this.getUserRoles(user).join(", ");
  }

  canListUsers(): boolean {
    return this.userPermissionsService.hasPermission("controllers.Users.findAll");
  }

  canCreateUsers(): boolean {
    return this.userPermissionsService.hasPermission("controllers.Users.create");
  }

  canUpdateUsers(): boolean {
    return this.userPermissionsService.hasPermission("controllers.Users.update");
  }

  canDeleteUsers(): boolean {
    return this.userPermissionsService.hasPermission("controllers.Users.delete");
  }

  canCreateRoles(): boolean {
    return this.userPermissionsService.hasAnyPermission(this.createRolePermissions);
  }

  canUpdateRoles(): boolean {
    return this.userPermissionsService.hasAnyPermission(this.updateRolePermissions);
  }

  canDeleteRoles(): boolean {
    return this.userPermissionsService.hasAnyPermission(this.deleteRolePermissions);
  }

  canSeeRoleActions(role: RoleOption): boolean {
    if (role.type === "system") {
      return true;
    }

    return this.canUpdateRoles() || this.canDeleteRoles();
  }

  canSeeUserActions(row: any): boolean {
    return (row.status_user === 1 && (this.canUpdateUsers() || this.canDeleteUsers() || this.canCreateUsers())) ||
      (row.status_user !== 1 && this.canUpdateUsers());
  }

  goBack() {
    this.router.navigate(["/my-portals"]);
  }

  memorizeForBack() {
    this.tableService.saveListState("onboarding-users-roles-list", {
      filter: this.filter,
      pageIndex: this.paginator ? this.paginator.pageIndex : 0,
      pageSize: this.paginator ? this.paginator.pageSize : 10,
    });
  }

  private loadRoles() {
    const partnerId = this.getPartnerId();
    if (!partnerId) {
      this.rolesDataSource.data = [];
      return;
    }

    this.rbacService.listRoles(partnerId).subscribe(
      (response) => this.setRolesFromResponse(response),
      (error) => {
        this.rolesDataSource.data = [];
        this.toast.showError(error);
      }
    );
  }

  private setRolesFromResponse(response: any) {
    const rawRoles = this.extractCollection(response);
    this.roles = rawRoles
      .map((role: any) => this.normalizeRole(role))
      .sort((firstRole, secondRole) => this.getRoleSortOrder(firstRole) - this.getRoleSortOrder(secondRole));
    this.rolesDataSource.data = this.roles;
  }

  private extractCollection(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.result)) return response.result;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.roles)) return response.data.roles;
    if (Array.isArray(response?.roles)) return response.roles;
    if (Array.isArray(response?.result?.data)) return response.result.data;
    if (Array.isArray(response?.result?.roles)) return response.result.roles;
    if (Array.isArray(response?.result?.data?.roles)) return response.result.data.roles;
    return [];
  }

  private extractUsers(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.result)) return response.result;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.users)) return response.data.users;
    if (Array.isArray(response?.users)) return response.users;
    if (Array.isArray(response?.result?.data)) return response.result.data;
    if (Array.isArray(response?.result?.users)) return response.result.users;
    if (Array.isArray(response?.result?.data?.users)) return response.result.data.users;
    return [];
  }

  private extractCreatedUserId(response: any): number | string | null {
    return response?.id ||
      response?.userId ||
      response?.result?.id ||
      response?.result?.userId ||
      response?.data?.id ||
      response?.data?.userId ||
      response?.result?.data?.id ||
      response?.result?.data?.userId ||
      null;
  }

  private normalizeRole(role: any): RoleOption {
    const name = role.name || role.roleName || role.nombre || this.translate.instant("onboardingUsersRoles.rolesTable.unnamedRole");
    const type = role.type || role.roleType || role.system || role.isSystem;
    const permissionsFromMenus = Array.isArray(role.menus)
      ? role.menus.reduce((total: number, menu: any) => total + (menu.permissions?.length || 0), 0)
      : 0;

    return {
      id: role.id || role.roleId || role.role_id,
      name,
      description: role.description || role.descripcion || "",
      permissionCount: role.permissionCount || role.permissionsCount || role.permissions?.length || permissionsFromMenus,
      userCount: role.userCount || role.usersCount || role.users?.length || 0,
      menus: role.menus || [],
      type: type === "system" ||
        type === "SYSTEM" ||
        type === true ||
        role.editable === false ||
        name === "Admin" ||
        name === "Manager" ||
        name === "User" ? "system" : "custom",
    };
  }

  private getRoleSortOrder(role: RoleOption): number {
    if (role.name === "Manager") return 0;
    if (role.name === "User") return 1;
    return 2;
  }

  private getPartnerId(): number | null {
    const partnerId = this.securityService.getTokenPartner();
    return partnerId ? Number(partnerId) : null;
  }
}
